import { useId } from "react";
import { Button } from "../design/components/Button";
import { Reason } from "../design/components/Reason";
import { useT } from "../i18n/locale";
import "./suspended-leads.css";

// «I suoi N Lead» (piano M2 Task 22, decisione del 26 settembre 2026 n. 5; mock-up `team.html`, finestra «Sospendi»):
// i Lead non archiviati di una persona che smette di lavorare, contati dal server (`GET /api/lead-owners/counts`). Nella
// finestra di sospensione si sceglie fra «Riassegnali agli altri User, a turno» e «Lasciali a lui per ora»; in «Gestisci»
// di chi è già sospeso o archiviato i Lead rimasti si riassegnano con un clic. Riassegnarli chiede di poter cambiare
// l'owner di tutti i Lead (questione aperta 77), e nella Vista Commerciale non si può: il server misura quel potere sul
// perimetro del Commerciale osservato. Quando non si può, il riquadro dice soltanto che restano a lui, segnalati, e perché.

export type LeadsChoice = "reassign" | "keep";

/**
 * Perché chi guarda non può riassegnarli: `power`, gli manca il potere (le funzioni `manage_team` e `change_owner` e la
 * modifica di tutti i Lead); `view`, la scheda è in una Vista Commerciale (questione aperta 119).
 */
export type ReassignBlock = "power" | "view";

type Common = {
  /** I Lead della persona, più di zero (senza Lead il riquadro non si mostra). */
  leads: number;
  /** `null` se chi guarda può riassegnarli, altrimenti il perché. */
  blocked: ReassignBlock | null;
};

type Props =
  | (Common & { mode: "choose"; choice: LeadsChoice; onChoice: (choice: LeadsChoice) => void })
  | (Common & { mode: "pending"; busy?: boolean; onReassign: () => void });

export function SuspendedLeadsBlock(props: Props) {
  const t = useT();
  const baseId = useId();
  const { leads, blocked } = props;
  const legend = leads === 1 ? t("team.leads.legendOne") : t("team.leads.legend", { count: leads });

  let body;
  if (blocked) {
    body = <Reason>{blocked === "view" ? t("team.leads.cannotReassignInView") : t("team.leads.cannotReassign")}</Reason>;
  } else if (props.mode === "pending") {
    body = (
      <>
        <p>{t("team.leads.pending")}</p>
        <p>
          <Button onClick={props.onReassign} disabled={props.busy} aria-busy={props.busy ? "true" : undefined}>
            {t("team.leads.reassign")}
          </Button>
        </p>
      </>
    );
  } else {
    const options: Array<{ value: LeadsChoice; label: string; hint: string }> = [
      { value: "reassign", label: t("team.leads.reassign"), hint: t("team.leads.reassignHint") },
      { value: "keep", label: t("team.leads.keep"), hint: t("team.leads.keepHint") },
    ];
    const { choice, onChoice } = props;
    body = options.map((option) => {
      const id = `${baseId}-${option.value}`;
      return (
        <div key={option.value} className="g-leads-left__option">
          <input
            id={id}
            type="radio"
            name={`${baseId}-choice`}
            value={option.value}
            checked={choice === option.value}
            onChange={() => onChoice(option.value)}
            aria-describedby={`${id}-hint`}
          />
          <label htmlFor={id}>{option.label}</label>
          <p id={`${id}-hint`} className="g-leads-left__hint">
            {option.hint}
          </p>
        </div>
      );
    });
  }

  return (
    <fieldset className="g-leads-left">
      <legend>{legend}</legend>
      {body}
    </fieldset>
  );
}
